/* ──────────────────────────────────────────────────────────────────────────
 *  Analytics type definitions — provider interface, event catalogue, and
 *  property shapes.  Every call-site imports from here; adapters implement
 *  the AnalyticsAdapter interface.
 * ────────────────────────────────────────────────────────────────────────── */

/* ── Ecommerce item (GA4 standard) ─────────────────────────────────────── */

export type EcommerceItem = {
  item_id: string;
  item_name: string;
  item_variant?: string;
  item_category?: string;
  item_brand?: string;
  price?: number;
  quantity?: number;
  currency?: string;
  index?: number;
  discount?: number;
};

/* ── Event map ─────────────────────────────────────────────────────────── */

export type AnalyticsEventMap = {
  /* --- Ecommerce (GA4 Enhanced Ecommerce) --- */
  view_item: { currency: string; value: number; items: EcommerceItem[] };
  view_item_list: {
    item_list_id?: string;
    item_list_name?: string;
    items: EcommerceItem[];
  };
  select_item: {
    item_list_id?: string;
    item_list_name?: string;
    items: EcommerceItem[];
  };
  add_to_cart: {
    currency: string;
    value: number;
    items: EcommerceItem[];
    /** Origin of the action — defaults to "self_serve" when omitted. Set
     *  to "agent" when the cart change was initiated by the voice agent
     *  (Phase 1 #7). */
    source?: "self_serve" | "agent";
  };
  remove_from_cart: {
    currency: string;
    value: number;
    items: EcommerceItem[];
    source?: "self_serve" | "agent";
  };
  view_cart: { currency: string; value: number; items: EcommerceItem[] };
  begin_checkout: {
    currency: string;
    value: number;
    items: EcommerceItem[];
    coupon?: string;
    source?: "self_serve" | "agent";
  };
  add_shipping_info: {
    currency: string;
    value: number;
    shipping_tier?: string;
    items: EcommerceItem[];
    source?: "self_serve" | "agent";
  };
  add_payment_info: {
    currency: string;
    value: number;
    payment_type?: string;
    items: EcommerceItem[];
    source?: "self_serve" | "agent";
  };
  purchase: {
    transaction_id: string;
    currency: string;
    value: number;
    tax?: number;
    shipping?: number;
    items: EcommerceItem[];
    source?: "self_serve" | "agent";
  };

  /* --- Auth --- */
  login: { method?: string };
  sign_up: { method?: string };
  logout: Record<string, never>;

  /* --- Search --- */
  search: { search_term: string; results_count?: number };
  view_search_results: { search_term: string; results_count?: number };

  /* --- Lead gen / forms --- */
  generate_lead: {
    form_name: string;
    form_source?: string;
    value?: number;
    currency?: string;
  };
  newsletter_signup: { optin_source: string };

  /* --- Engagement --- */
  page_view: { page_path: string; page_title?: string; page_referrer?: string };
  section_view: { section_id: string; page_path: string };
  scroll_depth: { depth_percent: number; page_path: string };
  cta_click: { cta_id: string; cta_text: string; page_path?: string };
  faq_expand: { question: string; section?: string };
  social_click: { platform: string; url: string };
  theme_toggle: { theme: string };
  cookie_consent: { action: "accept" | "reject" };
  view_blog_post: { post_id?: number; post_title: string; post_slug: string };
  filter_products: { filter_type: string; filter_value: string };
  sort_products: { sort_by: string };
  bmi_calculate: { bmi_value: number; bmi_category: string };
  document_upload: { document_type?: string };
  prescription_transfer: { pharmacy_name?: string };
  agent_open: { mode?: string };
  agent_message: { direction: "sent" | "received"; mode?: string };
  view_category: { category_name: string; category_slug: string };

  /* --- Agent action telemetry (Phase 1 #5) --------------------------
   * Strict PHI-free surface — see lib/analytics/agent.ts ALLOWED_PARAM_KEYS
   * for the per-event allowlist. Drug names, free-text queries, addresses,
   * emails, phones — none of these may appear here.
   * ------------------------------------------------------------------ */
  agent_session_start: {
    session_id: string;
    mode?: "voice" | "text";
    voice_backend?: string;
    page_path?: string;
    language?: string;
  };
  agent_session_end: {
    session_id: string;
    duration_sec: number;
    turn_count: number;
    tool_count: number;
    cost_usd_bucket: string;
    intent_classes?: string[];
    voice_backend?: string;
    end_reason?: string;
  };
  agent_tool_call: {
    tool_name: string;
    success: boolean;
    duration_ms_bucket: string;
    error_type?: string;
  };
  agent_search: {
    results_count: number;
    fallback_tier?: string;
    query_length_bucket: string;
  };
  agent_navigate: { target_path: string };
  agent_form_fill: { form_type: string };
  agent_error: { error_type: string; tool_name?: string };
  agent_mode_switch: { from: string; to: string };
  agent_interrupt: { target: "user_interrupted_agent" | "agent_interrupted" };
  agent_csat: { rating: "up" | "down"; has_comment: boolean };
  agent_quick_action_click: { action_id: string };
  agent_disambig_resolved: { candidates_count: number; picked_index?: number };
  agent_handoff_requested: { reason_class: string };
  agent_status: { tool_name: string; phase: "start" | "end" };

  /* --- Web Vitals --- */
  web_vitals: {
    metric_name: string;
    metric_value: number;
    metric_id: string;
    metric_rating: string;
  };
};

export type AnalyticsEventName = keyof AnalyticsEventMap;

/* ── Provider adapter interface ────────────────────────────────────────── */

export interface AnalyticsAdapter {
  /** Human-readable name (for debug logging). */
  name: string;

  /** Called once when the provider is registered. */
  init(): void;

  /**
   * Fire an event.  Adapters are free to rename / reshape the payload
   * for their platform.
   */
  track<E extends AnalyticsEventName>(
    event: E,
    properties: AnalyticsEventMap[E],
  ): void;

  /** Optional: identify a user (e.g. after login). */
  identify?(userId: string, traits?: Record<string, unknown>): void;

  /** Optional: reset identity (e.g. on logout). */
  reset?(): void;
}

/* ── Consent helpers ───────────────────────────────────────────────────── */

export const CONSENT_KEY = "cookie-consent";
export const CONSENT_EVENT = "cookie-consent-changed";

export function hasAnalyticsConsent(): boolean {
  if (typeof window === "undefined") return false;
  const showPopup =
    process.env.NEXT_PUBLIC_SHOW_CONSENT_POPUP === "true";
  if (!showPopup) return true;
  try {
    return localStorage.getItem(CONSENT_KEY) === "accepted";
  } catch {
    return false;
  }
}
